import { combineReducers } from 'redux'
import { call, put, select, takeLatest } from 'redux-saga/effects'

import VALUES from 'core/data-model/constants/values'
import { valid } from 'core/service/lib/check'
import { generatePlanCode, getInsuranceLoanTermByProductCode } from 'core/service/loan'
import { RESET_APPLICATION_STATE } from 'quick-quote/actions'
import { SELECT_GENDER, UPDATE_BIRTHDATE } from 'quick-quote/insured-information/actions'
import { getAge, getInsured } from 'quick-quote/insured-information/selectors'
import { EDIT_SUM_ASSURED } from 'quick-quote/product-common/coverage-plan/actions'
import {
  MRTA_UPDATE_LOAN_AMOUNT,
  MRTA_EDIT_LOAN_AMOUNT,
  MRTA_UPDATE_LOAN_PERIOD,
  MRTA_EDIT_LOAN_PERIOD,
  MRTA_UPDATE_COVERAGE_PERIOD,
  MRTA_EDIT_COVERAGE_PERIOD,
  MRTA_EDIT_PAYMENT_SELECTED_MRTA,
  MRTA_UPDATE_SUM_ASSURED_IN_LOAN,
  UPDATE_VALIDATE_COVERAGE_PERIOD_ERRORS,
  MRTA_EDIT_INTEREST_RATE,
  UPDATE_VALIDATE_INTEREST_RATE_ERRORS,
  MRTA_EDIT_SUMASSURED_CONTRACT,
} from 'quick-quote/product-mrta/actions'
import { SELECT_PRODUCT } from 'quick-quote/product-selection/actions'
import { getSelectedDisplayProduct, getSelectedDisplayProductCode } from 'quick-quote/product-selection/selectors'

// action

export const EDIT_LOAN_TERM = 'LOAN/EDIT_LOAN_TERM'

export const editLoanTerm = (loanTerm) => ({
  type: EDIT_LOAN_TERM,
  payload: loanTerm,
})

export const UPDATE_LOAN_TERM = 'LOAN/UPDATE_LOAN_TERM'

export const updateLoanTerm = (loanTermWithErrors) => ({
  type: UPDATE_LOAN_TERM,
  payload: loanTermWithErrors,
})

export const EDIT_LOAN_AMOUNT = 'LOAN/EDIT_LOAN_AMOUNT'

export const editLoanAmount = (loanAmount) => ({
  type: EDIT_LOAN_AMOUNT,
  payload: loanAmount,
})

export const UPDATE_LOAN_AMOUNT = 'LOAN/UPDATE_LOAN_AMOUNT'

export const updateLoanAmount = (loanAmountWithErrors) => ({
  type: UPDATE_LOAN_AMOUNT,
  payload: loanAmountWithErrors,
})

export const UPDATE_AVAILABLE_COVERAGE_TERM = 'LOAN/UPDATE_AVAILABLE_COVERAGE_TERM'

export const updateAvailableCoverageTerms = (availableCoverageTerms) => ({
  type: UPDATE_AVAILABLE_COVERAGE_TERM,
  payload: availableCoverageTerms,
})

export const UPDATE_SUM_ASSURED = 'LOAN/UPDATE_SUM_ASSURED'

export const updateSumAssured = (sumAssured) => ({
  type: UPDATE_SUM_ASSURED,
  payload: sumAssured,
})

export const UPDATE_SUM_ASSURED_ERRORS = 'LOAN/UPDATE_SUM_ASSURED_ERRORS'

export const updateSumAssuredErrors = (errors) => ({
  type: UPDATE_SUM_ASSURED_ERRORS,
  payload: errors,
})

export const UPDATE_COVERAGE_TERM = 'LOAN/UPDATE_COVERAGE_TERM'

export const updateCoverageTerm = (coverageTerm) => ({
  type: UPDATE_COVERAGE_TERM,
  payload: coverageTerm,
})

export const UPDATE_PAYMENT_METHOD = 'LOAN/UPDATE_PAYMENT_METHOD'

export const updatePaymentMethod = (paymentMethod) => ({
  type: UPDATE_PAYMENT_METHOD,
  payload: paymentMethod,
})

export const UPDATE_COVERAGE_TERM_ERRORS = 'LOAN/UPDATE_COVERAGE_TERM_ERRORS'

export const updateCoverageTermErrors = (errors) => ({
  type: UPDATE_COVERAGE_TERM_ERRORS,
  payload: errors,
})

export const UPDATE_PREMIUM = 'LOAN/UPDATE_PREMIUM'

export const updatePremium = (premium) => ({
  type: UPDATE_PREMIUM,
  payload: premium,
})

export const UPDATE_INSURANCE_LOAN_TERM = 'LOAN/UPDATE_INSURANCE_LOAN_TERM'

export const updateInsuranceLoanTerm = (insuranceLoanTerm) => ({
  type: UPDATE_INSURANCE_LOAN_TERM,
  payload: insuranceLoanTerm,
})

// reducer

const defaultNumberWithError = { value: 0, errors: [] }

const initialLoan = {
  amount: defaultNumberWithError,
  term: defaultNumberWithError,
  interestRate: valid(0),
  sumAssuredContract: 0,
}

const validDefault = valid(0)
const initialCoverage = {
  sumAssured: defaultNumberWithError,
  term: { ...validDefault, errors: [] },
  premium: 0,
  insuranceLoanTerm: 0,
  paymentMethod: VALUES.CASH_INCLUDE_LOAN,
}

export const loan = (state = initialLoan, action) => {
  switch (action.type) {
    case RESET_APPLICATION_STATE:
      return initialLoan
    case UPDATE_LOAN_TERM:
    case MRTA_UPDATE_LOAN_PERIOD:
    case MRTA_EDIT_LOAN_PERIOD:
      return { ...state, term: action.payload }
    case UPDATE_LOAN_AMOUNT:
    case MRTA_UPDATE_LOAN_AMOUNT:
    case MRTA_EDIT_LOAN_AMOUNT:
      return { ...state, amount: action.payload }
    case MRTA_EDIT_INTEREST_RATE:
      return { ...state, interestRate: { ...state.interestRate, value: action.payload } }
    case MRTA_EDIT_SUMASSURED_CONTRACT:
      return { ...state, sumAssuredContract: action.payload }
    case UPDATE_VALIDATE_INTEREST_RATE_ERRORS:
      return { ...state, interestRate: action.payload }
    default:
      return state
  }
}

export const coverage = (state = initialCoverage, action) => {
  switch (action.type) {
    case RESET_APPLICATION_STATE:
      return initialCoverage
    case UPDATE_AVAILABLE_COVERAGE_TERM:
      return { ...state, availableTerms: action.payload }
    case UPDATE_SUM_ASSURED:
    case MRTA_UPDATE_SUM_ASSURED_IN_LOAN:
    case EDIT_SUM_ASSURED:
      return { ...state, sumAssured: { ...state.sumAssured, value: action.payload } }
    case UPDATE_SUM_ASSURED_ERRORS:
      return { ...state, sumAssured: { ...state.sumAssured, errors: action.payload } }
    case UPDATE_COVERAGE_TERM:
    case MRTA_UPDATE_COVERAGE_PERIOD:
    case MRTA_EDIT_COVERAGE_PERIOD:
      return { ...state, term: { ...state.term, value: action.payload } }
    case UPDATE_PAYMENT_METHOD:
    case MRTA_EDIT_PAYMENT_SELECTED_MRTA:
      return { ...state, paymentMethod: action.payload }
    case UPDATE_COVERAGE_TERM_ERRORS:
      return { ...state, term: { ...state.term, errors: action.payload } }
    case UPDATE_PREMIUM:
      return { ...state, premium: action.payload }
    case UPDATE_INSURANCE_LOAN_TERM:
      return { ...state, insuranceLoanTerm: action.payload }
    case UPDATE_VALIDATE_COVERAGE_PERIOD_ERRORS:
      return { ...state, term: action.payload }
    default:
      return state
  }
}

export const reducer = combineReducers({ loan, coverage })

// selector

export const getLoanAmount = (state) => state.loan.coveragePlan.loan.amount.value
export const getLoanAmountErrors = (state) => state.loan.coveragePlan.loan.amount.errors
export const getLoanTerm = (state) => state.loan.coveragePlan.loan.term.value
export const getLoanTermErrors = (state) => state.loan.coveragePlan.loan.term.errors
export const getLoan = (state) => state.loan

export const getAvailableCoverageTerms = (state) => state.loan.coveragePlan.coverage.availableTerms
export const getPaymentMethod = (state) => state.loan.coveragePlan.coverage.paymentMethod
export const getSumAssured = (state) => state.loan.coveragePlan.coverage.sumAssured.value
export const getSumAssuredErrors = (state) => state.loan.coveragePlan.coverage.sumAssured.errors
export const getCoverageTerm = (state) => state.loan.coveragePlan.coverage.term.value
export const getCoverageTermErrors = (state) => state.loan.coveragePlan.coverage.term.errors
export const getPremium = (state) => state.loan.coveragePlan.coverage.premium

export const getInsuranceLoanTerm = (state) => state.loan.coveragePlan.coverage.insuranceLoanTerm

// saga

/**
 * createProductLoanCoverageFunc
 * @param {ReturnType<import('core/data-model/basic-plan/repository').createBasicPlanRepository>} basicPlanRepository
 * @param {ReturnType<import('core/service/basic-plan/rules/rules-validator').createBasicPlanRuleValidation>} basicPlanRuleValidationService
 * @param {ReturnType<import('core/service/loan/premium').createLoanPremiumService>} loanPremiumService
 */
export function createProductLoanCoverageFunc(basicPlanRepository, basicPlanRuleValidationService, loanPremiumService) {
  function* validateAndUpdateLoanAmount(action) {
    const value = action.payload
    const code = yield select(getSelectedDisplayProductCode)
    const validated = yield call(basicPlanRuleValidationService.validateLoanAmount, code, value)

    const errors = validated.isValid ? [] : [validated.message]

    yield put(updateLoanAmount({ value, errors }))
  }

  function* validateAndUpdateLoanTerm(action) {
    const selectedProduct = yield select(getSelectedDisplayProduct)
    if (selectedProduct.category !== VALUES.LOAN) {
      return
    }

    let value

    if (action.type === EDIT_LOAN_TERM) {
      value = action.payload
    } else {
      value = yield select(getLoanTerm)
    }
    const insuredAge = yield select(getAge)
    const code = yield select(getSelectedDisplayProductCode)

    const validated = yield call(basicPlanRuleValidationService.validateLoanTerm, code, value, insuredAge)
    const errors = validated.isValid ? [] : [validated.message]

    yield put(updateLoanTerm({ value, errors }))
  }

  function* doValidateSumAssured(action) {
    const displayProduct = yield select(getSelectedDisplayProduct)
    if (displayProduct.category !== VALUES.LOAN) return

    const sumAssured = yield select(getSumAssured)
    const loanAmount = yield select(getLoanAmount)
    const validated = yield call(
      basicPlanRuleValidationService.validateSumAssured,
      displayProduct.basicPlanCode,
      sumAssured,
      loanAmount
    )
    const errors = validated.isValid ? [] : [validated.message]

    yield put(updateSumAssuredErrors(errors))
  }

  function* doValidateCoverageTerm(action) {
    const displayProduct = yield select(getSelectedDisplayProduct)
    if (displayProduct.category !== VALUES.LOAN) return

    const loanTerm = yield select(getLoanTerm)
    const coverageTerm = yield select(getCoverageTerm)
    const validated = yield call(
      basicPlanRuleValidationService.validateCoverageTerm,
      displayProduct.basicPlanCode,
      coverageTerm,
      loanTerm
    )
    const errors = validated.isValid ? [] : [validated.message]
    yield put(updateCoverageTermErrors(errors))
  }

  function* onUpdateInsuranceLoanTerm() {
    const code = yield select(getSelectedDisplayProductCode)
    const coverageTerm = yield select(getCoverageTerm)
    const loanTerm = yield select(getLoanTerm)
    const insuranceLoanTerm = yield call(getInsuranceLoanTermByProductCode, code, { coverageTerm, loanTerm })

    yield put(updateInsuranceLoanTerm(insuranceLoanTerm))
  }

  function* setProductDefaults(action) {
    const displayProduct = action.payload
    if (displayProduct.category !== VALUES.LOAN) return

    const defaults = yield call(basicPlanRepository.getProductDefaults, displayProduct.basicPlanCode)
    if (defaults) {
      yield put(updateLoanAmount({ value: defaults.loanAmount, errors: [] }))
      yield put(updateLoanTerm({ value: defaults.loanTerm, errors: [] }))
      yield put(updateSumAssured(defaults.sumAssured))
      yield put(updateCoverageTerm(defaults.coverageTerm))
      yield put(updateAvailableCoverageTerms(defaults.availableTerms))
    }
  }

  function* onCalculatePremium() {
    const selectedProduct = yield select(getSelectedDisplayProduct)
    if (selectedProduct.category !== VALUES.LOAN) {
      return
    }
    const sumAssured = yield select(getSumAssured)
    const productCode = yield select(getSelectedDisplayProductCode)
    const coverageTerm = yield select(getCoverageTerm)
    const insuranceLoanTerm = yield select(getInsuranceLoanTerm)
    const paymentMethod = yield select(getPaymentMethod)
    const insured = yield select(getInsured)
    const planCode = yield call(generatePlanCode, selectedProduct.planCodeSchema, { coverageTerm, insuranceLoanTerm })
    const premiumParams = {
      sumAssured: sumAssured,
      productCode: productCode,
      paymentMethod: paymentMethod,
      planCode: planCode,
      insured: insured,
    }
    const premium = yield call(loanPremiumService.calculatePremium, premiumParams)

    yield put(updatePremium(premium))
  }

  return {
    validateAndUpdateLoanAmount,
    validateAndUpdateLoanTerm,
    doValidateSumAssured,
    doValidateCoverageTerm,
    onUpdateInsuranceLoanTerm,
    setProductDefaults,
    onCalculatePremium,
  }
}

/**
 * @param serviceWrapper {Awaited<ReturnType<import('core/service-wrapper/wrapper').buildServiceWrapper>>}
 */
function* watchers(serviceWrapper) {
  const { LOAN } = serviceWrapper.products

  const CALCULATE_PREMIUM_ACTION_TRIGGERS = [
    SELECT_GENDER,
    UPDATE_BIRTHDATE,
    UPDATE_INSURANCE_LOAN_TERM,
    UPDATE_SUM_ASSURED,
    UPDATE_LOAN_AMOUNT,
    UPDATE_PAYMENT_METHOD,
  ]

  yield [
    takeLatest(EDIT_LOAN_AMOUNT, LOAN.validateAndUpdateLoanAmount),
    takeLatest([EDIT_LOAN_TERM, UPDATE_BIRTHDATE], LOAN.validateAndUpdateLoanTerm),
    takeLatest([UPDATE_SUM_ASSURED, UPDATE_LOAN_AMOUNT], LOAN.doValidateSumAssured),
    takeLatest([UPDATE_COVERAGE_TERM, UPDATE_LOAN_TERM], LOAN.doValidateCoverageTerm),
    takeLatest([UPDATE_LOAN_TERM, UPDATE_COVERAGE_TERM], LOAN.onUpdateInsuranceLoanTerm),
    takeLatest(SELECT_PRODUCT, LOAN.setProductDefaults),
    takeLatest(CALCULATE_PREMIUM_ACTION_TRIGGERS, LOAN.onCalculatePremium),
  ]
}

export const sagas = watchers
